import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/types'

import Link from 'next/link'
import { useEffect, useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button-link'

interface SiteNavbarProps {
    variant?: 'bar' | 'floating'
    links: Array<NavItem>
    secondaryAction: NavItem
    primaryAction: NavItem
    currentHref?: string
}

interface MenuButtonProps {
    open: boolean
    controls: string
    onToggle: () => void
}

const linkClasses = 'relative flex items-center rounded-full px-[0.6rem] py-[0.6rem] text-base leading-[1.3] font-medium text-foreground transition-colors duration-300 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary aria-[current=page]:text-primary'

const MenuButton: FC<MenuButtonProps> = ({ open, controls, onToggle }): ReactNode => {
    return (
        <button
            type='button'
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={controls}
            aria-label={open ? 'Close menu' : 'Open menu'}
            className='flex size-12 items-center justify-center rounded-[10px] transition-colors duration-300 hover:bg-black/5 focus-visible:outline-2 focus-visible:outline-primary lg:hidden'
        >
            <span className='relative block h-4 w-[1.6rem]' aria-hidden='true'>
                <span
                    className={cn(
                        'absolute left-0 top-0 h-0.5 w-full rounded-full bg-foreground transition-transform duration-300',
                        open && 'translate-y-[7px] rotate-45'
                    )}
                />
                <span
                    className={cn(
                        'absolute left-0 top-[7px] h-0.5 w-full rounded-full bg-foreground transition-opacity duration-200',
                        open && 'opacity-0'
                    )}
                />
                <span
                    className={cn(
                        'absolute left-0 top-[14px] h-0.5 w-full rounded-full bg-foreground transition-transform duration-300',
                        open && '-translate-y-[7px] -rotate-45'
                    )}
                />
            </span>
        </button>
    )
}

export const SiteNavbar: FC<SiteNavbarProps> = ({
    variant = 'bar',
    links,
    secondaryAction,
    primaryAction,
    currentHref
}): ReactNode => {
    const [open, setOpen] = useState(false)
    const menuId = useId()
    const floating = variant === 'floating'

    const close = (): void => setOpen(false)

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        const onResize = (): void => {
            if (window.matchMedia('(min-width: 1024px)').matches) setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', onResize)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', onResize)
        }
    }, [open])

    const isCurrent = (href: string): boolean => href === currentHref

    const renderLinks = (stacked: boolean): ReactNode => links.map(link => (
        <li key={link.href} className={cn(stacked && 'w-full')}>
            <Link
                href={link.href}
                onClick={close}
                aria-current={isCurrent(link.href) ? 'page' : undefined}
                className={cn(linkClasses, stacked && 'justify-center py-3')}
            >
                {link.label}
            </Link>
        </li>
    ))

    const renderActions = (stacked: boolean): ReactNode => (
        <>
            <ButtonLink
                href={secondaryAction.href}
                variant='secondary'
                onClick={close}
                aria-current={isCurrent(secondaryAction.href) ? 'page' : undefined}
                className={cn(stacked && 'w-full')}
            >
                {secondaryAction.label}
            </ButtonLink>

            <ButtonLink href={primaryAction.href} onClick={close} className={cn(stacked && 'w-full')}>
                {primaryAction.label}
            </ButtonLink>
        </>
    )

    const mobilePanel = open && (
        <div
            id={menuId}
            className={cn(
                'absolute inset-x-0 top-full z-10 lg:hidden',
                floating ? 'mt-2.5 rounded-[12px] bg-background p-4 shadow-[0_18px_40px_-12px_rgb(15_16_19/0.25),inset_0_0_0_1px_rgb(0_0_0/0.08)] md:px-8 md:pt-6 md:pb-8' : 'border-b border-border bg-background px-4 pt-2 pb-8'
            )}
        >
            <nav aria-label='Main'>
                <ul className='flex flex-col items-center gap-1'>
                    {renderLinks(true)}
                </ul>
            </nav>

            <div className='mt-4 flex flex-col gap-2'>
                {renderActions(true)}
            </div>
        </div>
    )

    if (floating) {
        return (
            <header className='sticky top-0 z-50 -mb-20 px-2.5 py-2.5'>
                <div className='relative mx-auto flex h-15 w-full items-center justify-between rounded-tile bg-background pr-2.5 pl-4 shadow-[inset_0_0_0_1px_rgb(0_0_0/0.15)] lg:w-fit lg:min-w-[31.25rem] lg:justify-center'>
                    <div className='relative lg:mr-5 lg:pr-5'>
                        <Logo onClick={close} />
                        <span className='absolute inset-y-[0.3rem] right-0 hidden w-px bg-secondary lg:block' aria-hidden='true' />
                    </div>

                    <div className='hidden flex-1 items-center justify-end gap-2.5 lg:flex'>
                        <nav aria-label='Main'>
                            <ul className='flex items-center'>
                                {renderLinks(false)}
                            </ul>
                        </nav>

                        <div className='flex items-center gap-2.5'>
                            {renderActions(false)}
                        </div>
                    </div>

                    <MenuButton open={open} controls={menuId} onToggle={() => setOpen(value => !value)} />

                    {mobilePanel}
                </div>
            </header>
        )
    }

    return (
        <header className='sticky top-0 z-50 w-full bg-background'>
            <div className='relative mx-auto flex min-h-18 w-full max-w-site items-center justify-between px-4 md:px-8'>
                <Logo onClick={close} />

                <div className='hidden flex-1 items-center justify-end lg:flex'>
                    <nav aria-label='Main' className='pr-8'>
                        <ul className='flex items-center gap-1.5'>
                            {renderLinks(false)}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-2.5'>
                        {renderActions(false)}
                    </div>
                </div>

                <MenuButton open={open} controls={menuId} onToggle={() => setOpen(value => !value)} />

                {mobilePanel}
            </div>
        </header>
    )
}
